import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, MessageCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aurobindo-kids/artifacts/z058rndb_Aurobindo_Logo_WhiteBackgnd.png';
const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20an%20admission%20enquiry';

const quickLinks = [
  { label: 'About Our School', path: '/about' },
  { label: 'Montessori Method', path: '/montessori' },
  { label: 'Our Programs', path: '/programs' },
  { label: 'Admissions', path: '/admissions' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Activities', path: '/extra-curricular' },
  { label: 'Testimonials', path: '/testimonials' },
  { label: 'Alumni', path: '/alumni' },
  { label: 'Contact Us', path: '/contact' },
];

export default function Footer() {
  return (
    <footer className="bg-[#1E3A8A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="School Logo" className="h-12 w-12 object-contain rounded-xl" />
              <div>
                <p className="font-black text-base leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>Sri Aurobindo</p>
                <p className="text-xs text-blue-200 leading-tight">Montessori House of Children</p>
              </div>
            </div>
            <p className="text-blue-200 text-sm leading-relaxed mb-5">
              Joyful Montessori learning for independent children. Nurturing curious minds in Secunderabad since our founding.
            </p>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#128C7E] transition-all duration-200"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-widest text-blue-300 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-blue-100 hover:text-white text-sm transition-colors hover:underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-widest text-blue-300 mb-4">Programs</h4>
            <ul className="space-y-2">
              <li><Link to="/programs" className="text-blue-100 hover:text-white text-sm transition-colors hover:underline">Toddler (Ages 2–2.5)</Link></li>
              <li><Link to="/programs" className="text-blue-100 hover:text-white text-sm transition-colors hover:underline">Primary (Ages 2.5–6)</Link></li>
              <li><Link to="/programs" className="text-blue-100 hover:text-white text-sm transition-colors hover:underline">Elementary (Ages 6–12)</Link></li>
              <li><Link to="/admissions" className="text-blue-100 hover:text-white text-sm transition-colors hover:underline">Admission Enquiry</Link></li>
            </ul>
            <div className="mt-6">
              <h4 className="font-bold text-sm uppercase tracking-widest text-blue-300 mb-3">School Hours</h4>
              <div className="flex items-center gap-2 text-blue-100 text-sm">
                <Clock size={14} className="flex-shrink-0" />
              <span>Mon – Fri: 9:00 am – 3:30 pm</span>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-widest text-blue-300 mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-blue-100">
                <MapPin size={14} className="mt-0.5 flex-shrink-0 text-blue-300" />
                <span>62, Krishnapuri Colony, W Marredpally Rd, Lakshmi Nagar, West Marredpally, Secunderabad, Telangana 500026</span>
              </li>
              <li>
                <a href="tel:9246887272" data-testid="footer-phone1" className="flex items-center gap-2 text-sm text-blue-100 hover:text-white transition-colors">
                  <Phone size={14} className="text-blue-300" /> 9246887272
                </a>
              </li>
              <li>
                <a href="tel:9849069579" data-testid="footer-phone2" className="flex items-center gap-2 text-sm text-blue-100 hover:text-white transition-colors">
                  <Phone size={14} className="text-blue-300" /> 9849069579
                </a>
              </li>
              <li>
                <a href="mailto:sriaurobindo60@gmail.com" data-testid="footer-email" className="flex items-center gap-2 text-sm text-blue-100 hover:text-white transition-colors break-all">
                  <Mail size={14} className="text-blue-300 flex-shrink-0" /> sriaurobindo60@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-blue-700/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-blue-300 text-xs">
            © {new Date().getFullYear()} Sri Aurobindo Montessori House of Children School. All rights reserved.
	    <br />
	    Made with <span className="text-red-400">♥</span> by TrueNorth Advisory, Hyderabad, India.
          </p>
          <Link to="/admissions" className="text-xs text-blue-200 hover:text-white underline transition-colors">
            Apply for Admission
          </Link>
        </div>
      </div>
    </footer>
  );
}
